import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import type {} from "@mui/lab/themeAugmentation";

export const Colors = {
  primary: "#47ACEB",
  gradient: "transparent linear-gradient(269deg, #2E73BF 0%, #154179 100%) 0% 0% no-repeat padding-box",
  secondary: "#2D5A90",
  primaryOpacity: "rgba(71, 172, 235, 0.5)",
  text: "#999999",
  dark: "#121212",
  white: "#ffffff",
  title: "#2D5A90",
  linkGrayText: "#AFAFAF",
  success: "#47B742",
  error: "#B00020",
  warning: "#D1B00E",
  info: "#47ACEB",
};

export const theme = createTheme({
  palette: {
    primary: {
      main: Colors.primary,
    },
    secondary: {
      main: Colors.secondary,
    },
    background: {
      default: "#f9f9f9",
    },
  },
  typography: {
    fontFamily: ["Open Sans", "Roboto", "Helvetica neue", "Arial", "sans-serif"].join(","),
    h1: { fontSize: "2.4rem", fontWeight: 700, color: Colors.dark, letterSpacing: "-0.02rem" },
    h2: { fontSize: "1.5rem", fontWeight: 600, color: Colors.dark },
    h3: { fontSize: "1.3rem", fontWeight: 600, color: Colors.secondary, marginLeft: 16, marginBottom: 8 },
    h4: { fontSize: "1.05rem", fontWeight: 600, color: Colors.secondary },
    body1: { fontSize: "1rem" },
    caption: { fontSize: "1rem", color: Colors.text },
    body2: { fontSize: ".9rem" },
    subtitle1: { fontSize: "1.1rem" },
    subtitle2: { fontSize: ".7rem" },
    button: { fontSize: ".9rem" },
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        gutterBottom: {
          marginBottom: "1.3rem",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: Colors.secondary,
          display: "flex",
          fontSize: "1.3rem",
          fontWeight: 600,
          alignItems: "center",
          "& svg": {
            marginRight: "16px",
          },
          "& button svg": {
            color: Colors.secondary,
            marginRight: "0",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          margin: "0 16px",
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        position: "fixed",
      },
      styleOverrides: {
        root: {
          boxShadow: "none",
          background: Colors.gradient,
          color: "#ffffff",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          // Ancho de menu desplegable
          width: 300,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Color de iconos de acciones en tablas
          color: Colors.primary,
        },
      },
    },
    MuiCollapse: {
      styleOverrides: {
        root: {
          // padding para submenu de MasterData
          paddingLeft: 16,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "@media (max-width: 768px)": {
            "& .buttonText": {
              display: "none",
              marginLeft: 5,
            },
            minWidth: "0px",
            borderRadius: 100,
            marginRight: "16px",
            padding: "8px",
            "& .MuiButton-startIcon": {
              margin: 0,
              padding: 0,
            },
          },
          textTransform: "uppercase",
          letterSpacing: 1,
          // Espacio debajo del botón
          // marginBottom: 32
        },
        contained: {
          backgroundColor: Colors.primary,
          color: "white",
          "&:active": {
            // Quita la sombra onclick
            boxShadow: "none",
          },
        },
        outlined: {
          border: "1px solid transparent",
        },
        text: {
          color: Colors.linkGrayText,
        },
        sizeSmall: {
          padding: 2,
          fontSize: ".7rem",
        },
        sizeMedium: {
          padding: "8px 32px",
          fontSize: ".9rem",
        },
        sizeLarge: {
          padding: 15,
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          ".MuiButtonBase-root": {
            padding: "1px 10px",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: Colors.success,
          color: "white",
        },
        standardError: {
          backgroundColor: Colors.error,
          color: "white",
        },
        standardWarning: {
          backgroundColor: Colors.warning,
          color: "white",
        },
        standardInfo: {
          backgroundColor: Colors.info,
          color: "black",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.9rem",
          color: Colors.linkGrayText,
        },
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          "& ::placeholder": {
            fontSize: "0.9rem",
            color: Colors.linkGrayText,
          },
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        disableExtendRowFullWidth: false,
      },
      styleOverrides: {
        filterForm: {
          flexDirection: "column",
          "& > *:first-of-type": {
            alignItems: "flex-end",
          },
        },
        columnHeaderTitle: {
          lineHeight: "1.5",
          fontSize: "0.8rem",
          whiteSpace: "initial",
        },
        toolbarContainer: {
          "& .MuiInputBase-input": {
            width: "00px",
            transition: "all 300ms ease",
            "&: focus": {
              width: "100px",
            },
          },
          "& svg": {
            color: Colors.linkGrayText,
          },
          "& svg:hover": {
            color: Colors.primary,
          },
          alignItems: "flex-start",
          padding: 0,
          margin: 0,
          "& button": {
            "& span": {
              padding: 0,
              margin: 0,
            },
            paddingTop: 8,
            paddingBottom: 8,
            border: "1px solid",
            minWidth: 40,
            borderColor: Colors.linkGrayText,
            borderRadius: 6,
            margin: 0,
            marginLeft: 16,
          },
        },
        root: {
          // Borde exterior de la tabla
          headerHeight: 140,
          border: 0,
          minWidth: "300px",
          width: "100%",
          ".MuiDataGrid-cell:last-child": {
            backgroundColor: "white",
            borderLeft: "1px solid #e0e0e0",
            position: "sticky",
            right: 0,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid",
          borderColor: Colors.linkGrayText,
          backgroundColor: Colors.white,
          borderLeft: "6px solid",
          borderLeftColor: Colors.primary,
          transition: "all 250ms ease",
          // '&: hover': {
          // 	transform: 'scale(1.04)',
          // 	zIndex: 9,
          // },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          p: {
            fontSize: "0.8rem",
          },
          "&:last-child": {
            padding: 0,
          },
        },
      },
    },

    MuiTreeItem: {
      styleOverrides: {
        iconContainer: {
          width: "32px",
        },
        label: {
          ".MuiTypography-root": {
            fontSize: "0.8rem",
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          ".meterColumn": {
            paddingRight: "16px",
            textAlign: "end",
            maxWidth: "110px",
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          width: "100%",
          marginBottom: 16,
          ".MuiBox-root": {
            marginBottom: 16,
          },
          ".bullet": {
            listStyle: "none",
            // display: 'list-item',
            // 'list-style-type': 'disc',
            marginLeft: 20,
            "&::before": {
              content: '"\\2022"',
              color: Colors.secondary,
              fontWeight: "bold",
              display: "inline-block",
              width: "1rem",
              marginLeft: "-1rem",
            },
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
        size: "small",
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: "small",
        fullWidth: true,
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          ".MuiPaper-root": {
            padding: 16,
          },
          "& .MuiGrid-item": {
            marginTop: "0.5rem",
            marginBottom: "0.5rem",
          },
          ".cadastral_treeview": {
            maxHeight: "352px",
            flexGrow: 1,
            overflow: "auto",
          },
          ".cadastral_cardview": {
            maxHeight: "432px",
            flexGrow: 1,
            overflow: "auto",
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 3,
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          border: "2px solid #DFDFDF",
          borderRadius: "5px 5px 0 0",
          color: "#AFAFAF",
          backgroundColor: "white",
          "&.Mui-selected": {
            color: "black",
            border: "2px solid #AFAFAF",
            borderBottom: "0px",
          },
        },
      },
    },
  },
  spacing: 16,
  shape: {
    borderRadius: 6,
  },
});

